import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Layers, Users, ArrowRight } from 'lucide-react';
interface HallmarkSlide3Props {
  isDark?: boolean;
}
export function HallmarkSlide3({
  isDark = false
}: HallmarkSlide3Props) {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };
  return <motion.div className="h-full w-full overflow-y-auto overflow-x-hidden" variants={containerVariants} initial="hidden" animate="visible" data-id="element-41">
      <div className="min-h-full flex flex-col justify-center max-w-6xl mx-auto px-6 sm:px-12 py-20 sm:py-24" data-id="element-42">
        {/* Header */}
        <motion.div variants={itemVariants} className="text-center mb-12 sm:mb-16" data-id="element-43">
          <h1 className={`text-3xl sm:text-5xl font-serif font-medium mb-4 sm:mb-6 tracking-tight ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-44">
            Pilot Structure (Flexible by Design)
          </h1>
          <p className={`text-base sm:text-xl max-w-3xl mx-auto leading-relaxed ${isDark ? 'text-white/80' : 'text-heliona-text-gray'}`} data-id="element-45">
            Designed to scale based on Hallmark's preference
          </p>
        </motion.div>

        {/* Timeline */}
        <motion.div variants={itemVariants} className="mb-12 sm:mb-16" data-id="element-46">
          <div className={`rounded-3xl p-8 sm:p-10 ${isDark ? 'bg-white/[0.03] border border-white/10' : 'bg-white border border-heliona-beige'}`} data-id="element-47">
            <div className="flex flex-col md:flex-row items-center justify-between gap-8 relative" data-id="element-48">
              {/* Connector Line (Desktop) */}
              <div className={`hidden md:block absolute top-1/2 left-0 w-full h-0.5 -translate-y-1/2 z-0 ${isDark ? 'bg-white/10' : 'bg-heliona-beige'}`} data-id="element-49"></div>

              {[{
              title: 'Baseline',
              time: 'Week 1',
              icon: Calendar
            }, {
              title: 'Midpoint',
              time: 'Week 6',
              icon: Layers
            }, {
              title: 'Final',
              time: 'Week 12',
              icon: Users
            }].map((step, idx) => <div key={idx} className="relative z-10 flex flex-col items-center text-center bg-transparent" data-id="element-50">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 border-4 ${isDark ? 'bg-heliona-navy border-heliona-mint/20' : 'bg-white border-heliona-mint/20'} shadow-sm`} data-id="element-51">
                    <step.icon className={`w-6 h-6 ${isDark ? 'text-heliona-mint' : 'text-heliona-mint-dark'}`} data-id="element-52" />
                  </div>
                  <h3 className={`text-lg font-bold mb-1 ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-53">
                    {step.title}
                  </h3>
                  <p className={`text-sm font-medium ${isDark ? 'text-white/60' : 'text-heliona-text-gray'}`} data-id="element-54">
                    {step.time}
                  </p>
                </div>)}
            </div>
          </div>
        </motion.div>

        {/* Layers */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12 items-center" data-id="element-55">
          <motion.div variants={itemVariants} className="space-y-6" data-id="element-56">
            <h3 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-57">
              Recommended Layering Approach
            </h3>
            <ul className="space-y-4" data-id="element-58">
              {['Senior Leaders', 'Directors / Functional Heads', 'People Managers closest to change execution'].map((layer, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-59">
                  <div className={`mt-1 p-1 rounded-full ${isDark ? 'bg-heliona-mint/20' : 'bg-heliona-mint/10'}`} data-id="element-60">
                    <ArrowRight className={`w-4 h-4 ${isDark ? 'text-heliona-mint' : 'text-heliona-mint-dark'}`} data-id="element-61" />
                  </div>
                  <span className={`text-base sm:text-lg ${isDark ? 'text-white/80' : 'text-heliona-text-gray'}`} data-id="element-62">
                    {layer}
                  </span>
                </li>)}
            </ul>
          </motion.div>

          <motion.div variants={itemVariants} data-id="element-63">
            <div className={`rounded-3xl p-8 text-center ${isDark ? 'bg-heliona-orange/10 border border-heliona-orange/30' : 'bg-heliona-orange/5 border border-heliona-orange/20'}`} data-id="element-64">
              <p className={`text-lg sm:text-xl font-medium leading-relaxed ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-65">
                This pilot framework scales whether{' '}
                <span className="font-bold text-heliona-orange" data-id="element-66">5 leaders</span>{' '}
                or{' '}
                <span className="font-bold text-heliona-orange" data-id="element-67">
                  25+ leaders
                </span>{' '}
                participate.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>;
}